import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OpcionRecomendadorComponent } from '../../../ui/components/opcion-recomendador/opcion-recomendador.component';
import { DatosRecomendadorPropuesta, TipoCliente } from '../../model/salud.model';
import { OpcionRecomendador } from '../../../ui/model/opcion-recomendador.model';
import { CaCardComponent } from '@global-front-components/ui/card';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RecomendadorApiService } from '../../services/api/recomendador-api.service';
import { ColectivasAgrupacion, ConsultarIdOpcionRecomendador, ModoRecomendador, Recomendador } from '../../model/recomendador.model';
import { ArbolDecisionApiService } from '../../services/api/arbol-decision-api.service';
import { ArbolNodo } from '../../model/arbol-decision.model';
import { of, switchMap, tap } from 'rxjs';

@Component({
	selector: 'app-recomendador',
	standalone: true,
	imports: [
		CommonModule,
		OpcionRecomendadorComponent,
		CaCardComponent,
		TranslateModule],
	templateUrl: './recomendador.component.html',
	styleUrls: ['./recomendador.component.scss']
})
export class RecomendadorComponent implements OnInit {
	@Input() tiposCliente: TipoCliente[] = [];
	@Input() tipoDocumento: string;
	@Input() esDental = false;
	@Input() colectivaSenior: number;
	@Input() modoRecomendador: ModoRecomendador;
	navegacionDeshabilitada = false;
	@Output() colectivasChange = new EventEmitter<ColectivasAgrupacion>();
	@Output() idOpcionPrimerNivelChange = new EventEmitter<number>(); // necesario para la tarificacion de todos los productos AyC
	@Output() idOpcionInicialChange = new EventEmitter<number>(); // necesario para la tarificacion de todos los productos Cajas
	@Output() tipoClienteSeleccionadoChange = new EventEmitter<TipoCliente>();
	@Output() mostrarInfoSeniorYNormalChange = new EventEmitter<boolean>();
	@Output() datosRecomendadorPropuestaChange = new EventEmitter<DatosRecomendadorPropuesta>();

	codIdioma: string;
	recomendador: Recomendador | null = null;

	// Niveles dinámicos
	niveles: OpcionRecomendador[][] = [];                // niveles[0] = nivel 1
	selecciones: (OpcionRecomendador | null)[] = [];     // selecciones por índice

	// Para la sección de tipos de cliente (mapeo inicial)
	opcionesTiposCliente: OpcionRecomendador[] = [];
	tipoClienteSeleccionado?: OpcionRecomendador;

	colectivasAgrupacion: ColectivasAgrupacion | null = null;

	constructor(
		private recomendadorApiService: RecomendadorApiService,
		private arbolDecisionApiService: ArbolDecisionApiService,
		private translateService: TranslateService
	) { }

	ngOnInit(): void {
		this.codIdioma = this.translateService.currentLang || 'ES';
		this.opcionesTiposCliente = this.tiposCliente.map(tc => ({
			id: tc.idTipoCliente,
			codCategoria: 'TIPO_CLIENTE',
			titulo: tc.descTipoCliente,
			descripcion: ''
		}));
	}

	// ---- eventos de selección ----
	onSeleccionarTipoCliente(opcion: OpcionRecomendador) {
		this.tipoClienteSeleccionado = opcion;
		this.resetearDesdeNivel(0);
		this.tipoClienteSeleccionadoChange.emit(this.tiposCliente.find(tc => tc.idTipoCliente === opcion.id));

		if (this.esDental) {
			this.getColectivasDental(opcion);
		} else if (this.modoRecomendador === ModoRecomendador.SENIOR) {
			// Caso: solo senior → tarificar directamente sin recomendador
			this.navegacionDeshabilitada = true;
			this.autoSeleccionarOpcionesColectivaSenior(opcion);
		} else {
			// Modo normal y normal_y_senior
			this.fetchNivel(0, null);
		}
	}

	private autoSeleccionarOpcionesColectivaSenior(opcion: OpcionRecomendador) {
		this.obtenerIdOpcionInicial(opcion).pipe(
			switchMap(idOpcion => idOpcion
				? this.arbolDecisionApiService.getOpcionesArbol(this.recomendador!.idArbol, idOpcion)
				: of({ opciones: [] })
			)
		).subscribe(resp => {
			const opcionesNivel0 = this.mapArbolNodoToOpcionRecomendador(resp?.opciones ?? []);
			this.niveles[0] = opcionesNivel0;

			// seleccionar la opción 'SIN' si existe
			const seleccion = opcionesNivel0.find(o => o.titulo === 'SIN');
			if (!seleccion) {
				this.resetearDesdeNivel(0);
				return;
			}
			// establecer selección en nivel 0 y emitir id del primer nivel
			this.selecciones[0] = seleccion;
			this.getColectivasSenior(seleccion.id);
		});
	}

	private getColectivasSenior(idOpcion: number): void {
		if (!this.recomendador || !this.colectivaSenior) return;
		this.emitirDatosRecomendador(idOpcion);
		this.recomendadorApiService.getColectivaAgrupacion(
			this.recomendador.idRecomendador,
			idOpcion,
			this.translateService.currentLang?.toUpperCase() || 'ES',
			String(this.colectivaSenior),
			this.esDental
		).subscribe(colectivas => {
			if (colectivas) {
				this.colectivasAgrupacion = colectivas;
				this.colectivasChange.emit(colectivas);
			}
		});
	}


	private getColectivasDental(opcion: OpcionRecomendador) {
		// Obtener id inicial, luego consultar hijos y tarificar:
		this.obtenerIdOpcionInicial(opcion).subscribe(idOpcion => {
			if (idOpcion) {
				this.idOpcionInicialChange.emit(idOpcion);

				this.arbolDecisionApiService.getOpcionesArbol(this.recomendador!.idArbol, idOpcion)
					.subscribe(resp => {
						const primeraId = resp?.opciones?.[0]?.idOpcion ?? null;
						if (primeraId) {
							this.getColectivas(primeraId, idOpcion);
						} else {
							this.getColectivas(idOpcion);
						}
					});
			}
		});
	}

	onSeleccionarNivel(opcion: OpcionRecomendador, nivelIndex: number) {
		this.mostrarInfoSeniorYNormalChange.emit(false);
		this.selecciones[nivelIndex] = opcion;
		this.resetearDesdeNivel(nivelIndex + 1);
		// Si es el primer nivel (nivelIndex === 0), emitimos el idOpcion
		if (nivelIndex === 0 && opcion?.id) {
			this.idOpcionPrimerNivelChange.emit(opcion.id);
		}

		if (this.modoRecomendador === ModoRecomendador.NORMAL_CON_SENIOR && opcion.titulo === 'SIN') {
			this.getColectivasSenior(opcion.id);
			this.mostrarInfoSeniorYNormalChange.emit(true);
			return; // no seguir navegando
		}
		this.fetchNivel(nivelIndex + 1, opcion.id);
	}

	// ---- lógica genérica de niveles ----
	private resetearDesdeNivel(startIndex: number) {
		this.niveles.length = startIndex;
		this.selecciones.length = startIndex;
		this.colectivasAgrupacion = null;
		this.colectivasChange.emit(null);
	}

	private fetchNivel(levelIndex: number, idOpcionPadre: number | null) {
		// Nivel 0 requiere obtener recomendador e idOpcion inicial
		if (levelIndex === 0) {
			this.obtenerIdOpcionInicial().pipe(
				switchMap(idOpcion => {
					if (idOpcion) {
						this.idOpcionInicialChange.emit(idOpcion);
						return this.arbolDecisionApiService.getOpcionesArbol(this.recomendador!.idArbol, idOpcion);
					} else {
						return of({ opciones: [] });
					}
				})
			).subscribe(resp => {
				this.niveles[levelIndex] = this.mapArbolNodoToOpcionRecomendador(resp?.opciones ?? []);
			});
			return;
		}
		if (!this.recomendador) { return; }
		// Niveles > 0: pedir hijos del idOpcionPadre
		this.arbolDecisionApiService.getOpcionesArbol(this.recomendador.idArbol, idOpcionPadre)
			.subscribe(resp => {
				const mapped = this.mapArbolNodoToOpcionRecomendador(resp?.opciones ?? []);
				if (mapped.length) {
					this.niveles[levelIndex] = mapped;
				} else {
					// si no hay hijos, tarificar con la opción seleccionada del nivel anterior
					const anterior = this.selecciones[levelIndex - 1];
					if (anterior) { this.getColectivasRecomendadas(anterior.id); }
				}
			});
	}

	private mapArbolNodoToOpcionRecomendador(nodos: ArbolNodo[]): OpcionRecomendador[] {
		return nodos.map(n => ({
			id: n.idOpcion,
			titulo: (n.txtOpcion ?? '').toUpperCase(),
			descripcion: (n.txtCategoria ?? '').toUpperCase(),
			numHijos: n.numHijos,
			codCategoria: (n.codCategoria ?? '').toUpperCase()
		}));
	}

	private getColectivasRecomendadas(idOpcion: number) {
		this.emitirDatosRecomendador(idOpcion);
		this.recomendadorApiService.getColectivasRecomendadas(
			this.recomendador.idRecomendador,
			idOpcion,
			this.codIdioma,
			this.esDental
		).subscribe(colectivas => {
			this.setColectivasAgrupacion(colectivas);
		});
	}

	private setColectivasAgrupacion(colectivas: ColectivasAgrupacion) {
		this.colectivasAgrupacion = colectivas;
		this.colectivasChange.emit(colectivas);
	}

	private getColectivas(idOpcion: number, idOpcionFallback?: number) {
		this.emitirDatosRecomendador(idOpcion);
		this.recomendadorApiService.getColectivasAgrupacionesRecomendador(
			idOpcion,
			this.codIdioma,
			this.esDental
		).subscribe(colectivas => {
			if (colectivas?.colectivas?.length || colectivas?.colectivasAlternativas?.length) {
				this.setColectivasAgrupacion(colectivas);
			} else if (idOpcionFallback) {
				this.getColectivas(idOpcionFallback);
			}
		});
	}

	private emitirDatosRecomendador(idOpcion: number) {
		const datosRecomendador: DatosRecomendadorPropuesta = {
			idRecomendador: String(this.recomendador?.idRecomendador),
			idOpcion: String(idOpcion)
		};
		this.datosRecomendadorPropuestaChange.emit(datosRecomendador);
	}

	private obtenerIdOpcionInicial(opcion?: OpcionRecomendador) {
		return this.recomendadorApiService.getRecomendador().pipe(
			tap(r => this.recomendador = r),
			switchMap(r => {
				const tipoClienteDesc = this.tiposCliente.find(tc => tc.idTipoCliente === (opcion?.id ?? this.tipoClienteSeleccionado?.id))?.descTipoCliente ?? '';
				const consulta: ConsultarIdOpcionRecomendador = {
					idRecomendador: r.idRecomendador,
					tipoCliente: tipoClienteDesc,
					idioma: this.codIdioma,
					tipoDocumento: this.tipoDocumento || 'NIF'
				}
				return this.recomendadorApiService.getIdOpcionRecomendador(consulta);
			})
		);
	}
}
